import type { Workflow } from '@n8n-builder/n8n-types';
import type { CredentialRequirement } from '../store/credentials';

/** Extract credential requirements from a workflow's nodes. */
export function extractCredentialRequirements(workflow: Workflow): CredentialRequirement[] {
  const typeMap = new Map<string, Set<string>>();

  for (const node of workflow.nodes || []) {
    if (!node.credentials) continue;
    for (const credType of Object.keys(node.credentials)) {
      if (!typeMap.has(credType)) {
        typeMap.set(credType, new Set());
      }
      typeMap.get(credType)!.add(node.name);
    }
  }

  return Array.from(typeMap.entries()).map(([credentialType, nodeSet]) => ({
    credentialType,
    nodeNames: Array.from(nodeSet),
    isOAuth2: /oauth2/i.test(credentialType),
  }));
}

/** Normalize a string for fuzzy comparison: strip non-alphanumeric, lowercase. */
export function normalize(s: string): string {
  return s.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

/** Extract the service-name prefix from a credential type (before Auth/OAuth/Api/Token suffixes). */
export function getServicePrefix(credType: string): string {
  return credType
    .replace(/(OAuth\d*|Api|Auth|Token|Credentials?).*$/i, '')
    .toLowerCase();
}

/** Check if a sticky-note checkbox line matches a credential type. */
export function credentialTypeMatchesLine(credType: string, line: string): boolean {
  const normType = normalize(credType);
  const normLine = normalize(line);
  // Primary: full normalized type is a substring of the line
  if (normLine.includes(normType)) return true;
  // Fallback: service prefix appears in the line (e.g. "airtable" in "Airtable Personal Access Token")
  const prefix = getServicePrefix(credType);
  return prefix.length >= 3 && normLine.includes(prefix);
}

/** Update credential checkboxes in the Setup Checklist sticky note. */
export function updateStickyNoteCredentials(
  content: string,
  mappedCredTypes: Set<string>
): string {
  const lines = content.split('\n');
  let inCredentialsSection = false;

  return lines
    .map((line) => {
      if (line.startsWith('### ')) {
        inCredentialsSection = /credentials/i.test(line);
      }
      if (!inCredentialsSection || !/^- \[[ x]\] /.test(line)) return line;

      const isMatched = Array.from(mappedCredTypes).some((ct) =>
        credentialTypeMatchesLine(ct, line)
      );
      return isMatched
        ? line.replace('- [ ]', '- [x]')
        : line.replace('- [x]', '- [ ]');
    })
    .join('\n');
}

/** Apply mapped credentials to all nodes in a workflow. */
export function applyCredentialsToWorkflow(
  workflow: Workflow,
  requirements: CredentialRequirement[]
): Workflow {
  const credMap = new Map<string, { id: string; name: string }>();
  for (const req of requirements) {
    if (req.mappedCredential) {
      credMap.set(req.credentialType, req.mappedCredential);
    }
  }

  if (credMap.size === 0) return workflow;

  const mappedCredTypes = new Set(credMap.keys());

  return {
    ...workflow,
    nodes: (workflow.nodes || []).map((node) => {
      // Update sticky note checklist
      if (
        node.type === 'n8n-nodes-base.stickyNote' &&
        typeof node.parameters?.content === 'string' &&
        node.parameters.content.includes('Setup Checklist')
      ) {
        const updated = updateStickyNoteCredentials(
          node.parameters.content,
          mappedCredTypes
        );
        if (updated !== node.parameters.content) {
          return {
            ...node,
            parameters: { ...node.parameters, content: updated },
          };
        }
        return node;
      }

      // Update node credentials
      if (!node.credentials) return node;

      const updatedCreds = { ...node.credentials };
      for (const credType of Object.keys(updatedCreds)) {
        const mapped = credMap.get(credType);
        if (mapped) {
          updatedCreds[credType] = { id: mapped.id, name: mapped.name };
        }
      }
      return { ...node, credentials: updatedCreds };
    }),
  };
}
